/**
 * ZOY-637 — a typed integer field says what is wrong before anything is
 * sent, in words next to the field, and never lets an unusable value be
 * dropped on the floor. Ranges mirror the verified backend DTOs (the server
 * stays authoritative): room and practitioner buffers 0–240 minutes,
 * sessions per day 1–100, sessions per week 1–500.
 */

import type { IntlShape } from 'react-intl';
import { parseNumericInput } from '@/core/i18n/digits';

export interface IntegerRange {
  min: number;
  max: number;
}

export const BUFFER_RANGE: IntegerRange = { min: 0, max: 240 };
export const MAX_PER_DAY_RANGE: IntegerRange = { min: 1, max: 100 };
export const MAX_PER_WEEK_RANGE: IntegerRange = { min: 1, max: 500 };

export type IntegerProblem = 'digits' | 'whole' | 'range';

export interface IntegerField {
  /** The number to send; null when the field is empty (nothing typed) or unusable. */
  value: number | null;
  problem: IntegerProblem | null;
}

export function integerField(raw: string, range: IntegerRange): IntegerField {
  if (raw.trim() === '') return { value: null, problem: null };
  const n = parseNumericInput(raw);
  if (n === null) return { value: null, problem: 'digits' };
  if (!Number.isInteger(n)) return { value: null, problem: 'whole' };
  if (n < range.min || n > range.max) return { value: null, problem: 'range' };
  return { value: n, problem: null };
}

export function integerProblemText(
  intl: IntlShape,
  problem: IntegerProblem,
  range: IntegerRange,
): string {
  return intl.formatMessage({ id: `field.${problem}` }, { min: range.min, max: range.max });
}
